// The canonical Pomela brand mark. ONE mark across the favicon, the
// in-app surfaces, the boot splash, the crash screen, and the OG card.
// Renders the same SVG geometry as `apps/creator/public/icons/icon.svg`
// — that file is the source of truth; this component mirrors its
// shapes verbatim so we never ship a divergent mark again.
//
// Visual: a rounded brand tile filled with the warm→pink→violet gradient
// + a soft radial sheen at the top-left, with the lowercase "p" cut OUT
// as a stencil (the body of the letter is transparent, revealing the
// page underneath). The stencil reads pink against the gradient surround.
//
// The export name `PomelaMascot` is kept so existing call sites
// (NavRail, Sidebar, AuthWall, Settings TopBar, Onboarding) keep
// working without an import change. The `size` prop scales the entire
// mark and the contact shadow proportionally.
import type { JSX } from 'preact';
import { useId, useRef } from 'preact/hooks';

interface PomelaMascotProps {
  size?: number;
  /** When true, drops the soft contact-shadow ellipse beneath the tile.
   *  Use inside dense chrome where the shadow adds noise. */
  flat?: boolean;
}

// Module-level monotonic counter — GUARANTEES a unique suffix per mounted
// mascot within a page session. useId() alone was NOT enough: Preact's useId
// can return the SAME value ("P0") for two mascots mounted in separate render
// passes / subtrees (e.g. the sidebar mark + the AuthWall mark), so both svgs
// got id `pm-P0-pcut`. SVG url(#…) refs resolve document-wide to the FIRST
// matching id, and the sidebar's mask lives in a display:none drawer (zero-size)
// — so the AuthWall's white stencil rect resolved to that empty mask and
// rendered as a SOLID WHITE SQUARE (the reported broken-logo bug). Combining
// useId (hydration-stable) with this counter (collision-proof) fixes it.
let __pmSeq = 0;

export function PomelaMascot({ size = 96, flat = false }: PomelaMascotProps): JSX.Element {
  // useId for hydration stability + a module counter for guaranteed uniqueness.
  // Strip non-alphanumerics so the result is a valid SVG id in url(#…) refs.
  const uid = useId().replace(/[^a-z0-9]/gi, '');
  const seqRef = useRef<number | null>(null);
  if (seqRef.current === null) seqRef.current = __pmSeq++;
  const id = `pm-${uid}-${seqRef.current}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      role="img"
      aria-label="Pomela"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={`${id}-tile`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#FF9F5A" />
          <stop offset="0.52" stop-color="#F4477E" />
          <stop offset="1" stop-color="#7C4DFF" />
        </linearGradient>
        <radialGradient id={`${id}-sheen`} cx="30%" cy="22%" r="78%">
          <stop offset="0" stop-color="#FFFFFF" stop-opacity="0.38" />
          <stop offset="0.62" stop-color="#FFFFFF" stop-opacity="0" />
        </radialGradient>
        {/* Stencil mask: white = visible tile, black = transparent
            cutout. The "p" reads as a rect stem + a ring bowl (filled
            white minus a black inner circle) + a small highlight dot. */}
        <mask id={`${id}-pcut`}>
          <rect width="512" height="512" fill="#000" />
          <rect x="141" y="124" width="52" height="276" rx="26" fill="#fff" />
          <circle cx="263" cy="224" r="108" fill="#fff" />
          <circle cx="263" cy="224" r="50" fill="#000" />
          <circle cx="281" cy="208" r="21" fill="#fff" />
        </mask>
      </defs>

      {/* Optional grounding shadow — only on hero placements (auth wall,
          onboarding). Skipped in dense chrome via the `flat` prop. */}
      {!flat && (
        <ellipse cx="256" cy="486" rx="148" ry="14" fill="#F4477E" opacity="0.14" />
      )}

      {/* Gradient tile base. */}
      <rect width="512" height="512" rx="112" fill={`url(#${id}-tile)`} />
      {/* Top-left sheen for dimensionality. */}
      <rect width="512" height="512" rx="112" fill={`url(#${id}-sheen)`} />
      {/* Stencil pass: the "p" is CUT from a white rect masked by `p-cut`.
          The negative space reveals the gradient tile beneath, so the
          letter reads as the brand gradient against… itself? No —
          actually the rect is white and the mask hides everything but
          the "p" shape, so the visible "p" is white on the gradient
          tile. Matches the icon.svg verbatim. */}
      <rect width="512" height="512" fill="#fff" mask={`url(#${id}-pcut)`} />
    </svg>
  );
}
